import { Stack, Box, Typography, Button, Divider, useTheme } from '@mui/material'
import _ from 'lodash/fp'
import { Error } from 'components/Error'
import { Loading } from 'components/Loading'
import PageWrapper from 'hoc/PageWrapper'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PublishForm, PublishFormData } from './publish-form'
import { ProcurementForm } from './procurement-form'
import { ProcurementPlansTable } from './procurement-plans-table'
import { IProcurementPlanItem } from 'types/procurementPlans/procurementPlanItems'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { IFile } from 'types/file'
import {
  useProcurementPlanQuery,
  usePublishProcurementPlanMutation,
  useCreateProcurementPlanDraftMutation,
} from 'services/procurementPlans'
import { EProcurementPlanChangeReason } from 'types/procurementPlans/procurementPlanLogs'
import { useGetFileQuery } from 'services/file'
import { EProcurementPlanStatus, IProcurementPlan } from 'types/procurementPlans'
import { useDeleteProcurementPlanItemMutation } from 'services/procurementPlans/procurementPlanItems'
import { DownloadPlanTemplate } from 'pages/procurement-plans/download-plan-template'
import { UploadExcelTemplate } from 'pages/procurement-plans/upload-excel-template'

type Props = {
  procurementPlan: IProcurementPlan
  refetch: () => void
}

const EditProcurementPlan = ({ procurementPlan, refetch }: Props) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()

  const { data: creationOrderFile } = useGetFileQuery(
    { id: procurementPlan?.creationOrderFileId ?? 0 },
    { skip: !procurementPlan?.creationOrderFileId }
  )
  const [publishProcurementPlan] = usePublishProcurementPlanMutation()
  const [createProcurementPlanDraft] = useCreateProcurementPlanDraftMutation()
  const [deleteProcurementPlanItem] = useDeleteProcurementPlanItemMutation()

  const [initialDataSet, setInitialDataSet] = useState(false)
  const [publishFormVisible, setPublishFormVisible] = useState(false)
  const [procurementFormVisible, setProcurementFormVisible] = useState(false)
  const [procurementPlans, setProcurementPlans] = useState<IProcurementPlanItem[]>(
    []
  )
  const [currentlyEditingPlan, setCurrentlyEditingPlan] =
    useState<IProcurementPlanItem | null>(null)

  const defaultPublishFormData: Partial<PublishFormData> = {
    file: creationOrderFile,
    reasonDescription: procurementPlan?.reasonDescription,
    changeReason: procurementPlan?.changeReason,
    creationOrderDate: procurementPlan?.creationOrderDate,
  }

  useEffect(() => {
    if (procurementPlan && !initialDataSet) {
      setProcurementPlans(procurementPlan.procurementPlanItems ?? [])
      setInitialDataSet(false)
    }
  }, [initialDataSet, procurementPlan])

  const handlePublishClick = () => {
    setPublishFormVisible(true)
  }

  const handleDraftSave = () => {
    createProcurementPlanDraft({
      ...procurementPlan,
      procurementPlanItems: procurementPlans.map((item) => ({
        ...item,
        id: _.isString(item.id) ? item.id : uuidv4(),
        executionDurationInDays: item.executionDurationInDays ?? '0',
      })),
    }).then((res) => {
      if (!('error' in res)) {
        toast.success(t('operation-success'))
      }
    })
  }

  const handleCreateEditModalClose = () => {
    setProcurementFormVisible(false)
    setCurrentlyEditingPlan(null)
  }

  const handleMissingFileAddition = (plan: IProcurementPlanItem, file: IFile) => {
    setProcurementPlans((old) =>
      old.map((i) => {
        if (i.id === plan.id) {
          return {
            ...i,
            procurementTable: file,
          }
        }

        return plan
      })
    )
  }

  const handlePublish = async (publishData: PublishFormData) => {
    setPublishFormVisible(false)
    if (procurementPlan) {
      const result = await publishProcurementPlan({
        archive: procurementPlan.status !== EProcurementPlanStatus.PLANNING,
        body: {
          ...procurementPlan,
          changeReason: publishData.changeReason as EProcurementPlanChangeReason,
          reasonDescription: publishData.reasonDescription,
          creationOrderDate: (_.isString(publishData.creationOrderDate)
            ? publishData.creationOrderDate
            : publishData.creationOrderDate ??
              procurementPlan.creationOrderDate) as string,
          status: EProcurementPlanStatus.PUBLISHED,
          creationOrderFileId:
            publishData.file?.id ?? procurementPlan.creationOrderFileId,
          procurementPlanItems: procurementPlans.map((plan) => ({
            ...plan,
            id: _.isString(plan.id) ? plan.id : uuidv4(),
            executionDurationInDays: plan.executionDurationInDays ?? '0',
          })),
          archived: false,
        },
      })

      if ('data' in result) {
        navigate(`/portal/procurement-plans/${result.data.id}/view`)
      }
    }
  }

  const handleCancelClick = () => {
    if (window.confirm(t('cancel-procurement-plan-changes-message'))) {
      navigate(-1)
    }
  }

  return (
    <PageWrapper>
      <Stack direction="row" alignItems="center">
        <Typography variant="h4" textTransform="uppercase" marginRight={8}>
          {t('procurement-plan')} {procurementPlan?.year}
        </Typography>
        <Stack alignItems="center" direction="row">
          <Typography variant="body1">{t('status')}:</Typography>
          <Typography fontWeight={700} color={theme.palette.primary.main}>
            {t(`procurement-status-${procurementPlan?.status}`)}
          </Typography>
        </Stack>
      </Stack>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginTop={2}
      >
        <Box />
        <Box display="flex" gap={2}>
          {procurementPlan.status === EProcurementPlanStatus.PLANNING && (
            <Button
              color="primary"
              variant="contained"
              style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
              onClick={handleDraftSave}
            >
              {t('save')}
            </Button>
          )}
          <Button
            disabled={!procurementPlans.length}
            color="primary"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={handlePublishClick}
          >
            {t('publish')}
          </Button>
          <Button
            color="warning"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={handleCancelClick}
          >
            {t('cancel')}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box display="flex" justifyContent="flex-end" marginBottom={1.5} gap={2}>
        <Button
          color="primary"
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          onClick={() => setProcurementFormVisible(true)}
        >
          {t('add-new-plan')}
        </Button>

        <DownloadPlanTemplate id={procurementPlan.id} />

        <UploadExcelTemplate onSuccess={refetch} id={procurementPlan.id} />
      </Box>

      <ProcurementPlansTable
        plans={procurementPlans}
        onEdit={(plan) => {
          setCurrentlyEditingPlan(plan)
          setProcurementFormVisible(true)
        }}
        onDelete={(planId) => {
          setProcurementPlans((prevPlans) =>
            prevPlans.filter((plan) => plan.id !== planId)
          )
          deleteProcurementPlanItem({
            procumentPlanId: procurementPlan.id,
            id: planId,
          })
        }}
        onAddFile={handleMissingFileAddition}
      />

      {publishFormVisible && (
        <PublishForm
          defaultValues={defaultPublishFormData}
          onCreate={handlePublish}
          handleClose={() => setPublishFormVisible(false)}
        />
      )}
      {procurementFormVisible && (
        <ProcurementForm
          handleClose={handleCreateEditModalClose}
          onProcurementPlanItemCreate={(item) => {
            if (item.id) {
              setProcurementPlans((prevPlans) => [
                ...prevPlans.filter((i) => i.id !== item.id),
                { ...item },
              ])
            } else {
              setProcurementPlans((prevPlans) => [
                ...prevPlans,
                { ...item, id: uuidv4() },
              ])
            }
          }}
          plan={currentlyEditingPlan}
        />
      )}
    </PageWrapper>
  )
}

export const EditProcurementPlanPage = () => {
  const { procurementId } = useParams()

  const {
    data: procurementPlan,
    isLoading,
    isError,
    refetch,
  } = useProcurementPlanQuery({ id: procurementId ?? '' }, { skip: !procurementId })

  if (isLoading) return <Loading />

  if (isError) return <Error />

  if (procurementPlan === undefined) {
    return <></>
  }

  return <EditProcurementPlan refetch={refetch} procurementPlan={procurementPlan} />
}
